// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * Props:
 *  * navigationOutSide: To add arrows out side swiper container, to fix overflow issue.
 *  * xController: To create custom controller.
 *  * extendedStyles: to append style to the component
 *
 *
 */

/* eslint-disable spire/fenced-imports */
import SwiperCore, { A11y, Autoplay, Controller, Navigation, Pagination, Scrollbar } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
/* eslint-enable spire/fenced-imports */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import ArrowNavigation from "@plumberssupply/App/Components/XngCarousel/ArrowNavigation";
import { SwiperContext } from "@plumberssupply/App/Components/XngCarousel/XngCarousel.context";
import { xSlidesPerView } from "@plumberssupply/App/Components/XngCarousel/XngCarousel.helper";
import { SwiperInstance, XSwiper } from "@plumberssupply/App/Components/XngCarousel/XngCarousel.models";
import { xSwiperStyle } from "@plumberssupply/App/Components/XngCarousel/XngCarousel.style";
import { color } from "@plumberssupply/XDL/Utilities";
import { mergeWithCss } from "@plumberssupply/XDL/Utilities/mergeWithCss";
import cloneDeep from "lodash/cloneDeep";
import pick from "lodash/pick";
import React, { FC, isValidElement, useEffect, useState } from "react";
import { css } from "styled-components";

SwiperCore.use([Navigation, Pagination, Scrollbar, A11y, Controller, Autoplay]);

export type Props = Swiper & {
    /** callback when xSlidesChange */
    onSlidesPerView?: (slidesPerView?: number) => void;
    /**
     * To enable xSlidesPerView:
     * 1. add xSlidesPerView: true
     * 2. don't pass slidesPerView and breakpoint to XngCarousel
     *  */
    xSlidesPerView?: boolean;
    maximumNumOfSlidesPerView?: number;
    extendedStyles?: SwiperStyle;
    showSecondaryArrows?: boolean;
    productImageCarousel?: boolean;
};

export interface SwiperStyle {
    container?: InjectableCss;
}

// project style
const swiperStyle: SwiperStyle = {
    container: {
        css: css`
            ${xSwiperStyle}

            .xng-pagination-container {
                position: relative;
                display: flex;
                align-items: center;
                justify-content: center;
            }

            .swiper-pagination {
                position: relative;
                text-align: center;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-wrap: wrap;

                &.swiper-pagination-lock {
                    display: none;
                }

                .swiper-pagination-bullet {
                    width: 10px;
                    height: 10px;
                    margin: 8px 4px;
                    background: ${color("common.white")};
                    border: 1px solid ${color("common.ui8")};
                    opacity: 1;
                    outline: none;

                    &:focus {
                        outline: none;
                    }

                    &:hover:not(.swiper-pagination-bullet-active) {
                        background: ${color("secondary")};
                    }
                    &.swiper-pagination-bullet-active {
                        width: 10px;
                        height: 10px;
                        background: ${color("text")};
                        border: 1px solid ${color("text")};
                    }
                }
            }
        `,
    },
};

const XngCarousel: FC<Props> = props => {
    const {
        children,
        className,
        maximumNumOfSlidesPerView,
        xSlidesPerView: xSlidesPerViewEnable,
        onSlidesPerView,
        extendedStyles,
        showSecondaryArrows,
        productImageCarousel,
        ...otherProps
    } = props;

    const [isSwiperCssImported, setIsSwiperCssImported] = useState(false);
    const [swiperInstance, setSwiperInstance] = useState<SwiperInstance | null>(null);
    const [styles] = useState(() => mergeWithCss(swiperStyle, extendedStyles));

    useEffect(() => {
        const getStyle = async () => {
            /* eslint-disable global-require */
            await require("swiper/swiper-bundle.min.css");
            /* eslint-enable global-require */
        };
        getStyle().then(_ => setIsSwiperCssImported(true));
    }, []);

    /**
     * call when swiper created
     * @param swiper
     */
    const initSwiper = (swiper: XSwiper) => {
        setSwiperInstance(swiper);
        xSlidesPerView({
            swiper,
            maximumNumOfSlidesPerView,
            onSlidesPerView,
            enable: !(otherProps.slidesPerView || otherProps.breakpoints) && xSlidesPerViewEnable,
        });

        // Add "position: relative" for element before carousel wrapper
        if (swiper.el.parentElement?.parentElement) {
            swiper.el.parentElement.parentElement.style.position = "relative";
        }
    };

    if (!isSwiperCssImported) {
        return null;
    }

    return (
        <SwiperContext.Provider value={swiperInstance}>
            <StyledWrapper {...styles.container} className={`carousel-main-wrapper ${className || ""}`}>
                {otherProps.navigation && showSecondaryArrows && (
                    <ArrowNavigation
                        arrowType="prev"
                        showOnMobile={false}
                        showSecondaryArrows={showSecondaryArrows}
                        productImageCarousel={productImageCarousel}
                    />
                )}
                <Swiper
                    {...otherProps}
                    onSwiper={swiper => {
                        initSwiper(swiper);
                        otherProps.onSwiper?.(swiper);
                    }}
                    onSlideChange={swiper => {
                        setSwiperInstance(cloneDeep(swiper));
                        otherProps.onSlideChange?.(swiper);
                    }}
                    onResize={swiper => {
                        setSwiperInstance(cloneDeep(swiper));
                        otherProps.onResize?.(swiper);
                    }}
                >
                    {React.Children.map(children, child => {
                        // for null case
                        if (!isValidElement(child)) {
                            return;
                        }

                        const { props } = child;
                        const swiperSlidePropsKeys = ["tag", "zoom", "virtualIndex"];
                        const childrenProps = omitMultiple(props, [...swiperSlidePropsKeys]);
                        const swiperSlideProps = pick(props, swiperSlidePropsKeys);

                        return (
                            <SwiperSlide {...swiperSlideProps}>
                                {React.createElement(child.type, childrenProps)}
                            </SwiperSlide>
                        );
                    })}

                    {(otherProps.pagination || otherProps.navigation) && (
                        <StyledWrapper className="xng-pagination-container">
                            {otherProps.navigation && !showSecondaryArrows && (
                                <ArrowNavigation arrowType="prev" showOnMobile={true} />
                            )}
                            {otherProps.pagination && <div className="swiper-pagination" />}
                            {otherProps.navigation && !showSecondaryArrows && (
                                <ArrowNavigation arrowType="next" showOnMobile={true} />
                            )}
                        </StyledWrapper>
                    )}
                    {otherProps.scrollbar && <div className="swiper-scrollbar" />}
                </Swiper>
                {otherProps.navigation && showSecondaryArrows && (
                    <ArrowNavigation
                        arrowType="next"
                        showOnMobile={false}
                        showSecondaryArrows={showSecondaryArrows}
                        productImageCarousel={productImageCarousel}
                    />
                )}
            </StyledWrapper>
        </SwiperContext.Provider>
    );
};

XngCarousel.defaultProps = {
    xSlidesPerView: true,
    navigation: true,
    simulateTouch: false,
    spaceBetween: 20,
    pagination: { clickable: true, el: ".swiper-pagination" },
    watchOverflow: true,
    updateOnWindowResize: true,
    centerInsufficientSlides: true,
};

export default XngCarousel;
